import { useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import {
  SUBTYPES_BY_HOLDING_TYPE,
  holdingsApi,
  type Holding,
  type HoldingCreate,
  type HoldingPatch,
  type HoldingSubtype,
  type HoldingType,
} from '@/api/holdings';
import { ErrorCodes, isApiError } from '@/api/errors';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { toCivil } from '@/features/envelope/window';
import { bpToInputText, minorToInputText, parseMoneyToMinor, percentToBp } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import { isAccountNotFound } from './holdingFieldErrors';
import { GuardedDialog } from '@/components/GuardedDialog';
import { KindChip } from '@/components/KindChip';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';

const TITLE_ID = 'holding-editor-title';
const NAME_MAX = 120;
const DESC_MAX = 500;
const SYMBOL_MAX = 32;

interface HoldingEditorDialogProps {
  type: HoldingType;
  holding?: Holding;
  accounts: Account[];
  operatingCurrency: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

interface FormState {
  name: string;
  subtype: HoldingSubtype;
  description: string;
  acquiredOn: string;
  acquisitionCost: string;
  valuation: string;
  valuationOn: string;
  rate: string;
  financedByAccountId: string;
  symbol: string;
  quantity: string;
  costPerUnit: string;
  currentPrice: string;
  currentPriceAsOf: string;
}

type FieldKey =
  | 'name'
  | 'description'
  | 'acquiredOn'
  | 'acquisitionCostMinor'
  | 'valuationMinor'
  | 'valuationOn'
  | 'annualRateBp'
  | 'financedByAccountId'
  | 'symbol'
  | 'quantity'
  | 'costPerUnitMinor'
  | 'currentPriceMinor'
  | 'currentPriceAsOf';

const HOLDING_FIELDS = [
  'name',
  'description',
  'acquiredOn',
  'acquisitionCostMinor',
  'valuationMinor',
  'valuationOn',
  'annualRateBp',
  'financedByAccountId',
  'symbol',
  'quantity',
  'costPerUnitMinor',
  'currentPriceMinor',
  'currentPriceAsOf',
] as const satisfies readonly FieldKey[];

function quantityToInput(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 8, useGrouping: false }).format(
    value,
  );
}

function parseQuantity(input: string, locale: string): number | null {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  const group = parts.find((p) => p.type === 'group')?.value ?? ',';
  const decimal = parts.find((p) => p.type === 'decimal')?.value ?? '.';
  let cleaned = '';
  for (const ch of input) {
    if (/\d/.test(ch) || ch === '-' || ch === group || ch === decimal) cleaned += ch;
  }
  if (cleaned.length === 0 || cleaned.startsWith('-')) return null;
  cleaned = cleaned.split(group).join('');
  const pieces = cleaned.split(decimal);
  if (pieces.length > 2) return null;
  const fraction = pieces[1] ?? '';
  if (fraction.length > 8) return null;
  const n = Number(`${pieces[0] === '' ? '0' : pieces[0]}.${fraction}`);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function todayCivil(): string {
  return toCivil(new Date());
}

function initialState(
  type: HoldingType,
  holding: Holding | undefined,
  currency: string | null,
  locale: string,
): FormState {
  const subtype = holding?.subtype ?? SUBTYPES_BY_HOLDING_TYPE[type][0]!;
  return {
    name: holding?.name ?? '',
    subtype,
    description: holding?.description ?? '',
    acquiredOn: holding?.acquiredOn ?? '',
    acquisitionCost:
      holding?.acquisitionCostMinor != null
        ? minorToInputText(holding.acquisitionCostMinor, currency, locale)
        : '',
    valuation:
      holding?.valuationMinor != null
        ? minorToInputText(holding.valuationMinor, currency, locale)
        : '',
    valuationOn: holding?.valuationOn ?? todayCivil(),
    rate: holding?.annualRateBp != null ? bpToInputText(holding.annualRateBp, locale) : '',
    financedByAccountId:
      holding?.financedByAccountId != null ? String(holding.financedByAccountId) : '',
    symbol: holding?.symbol ?? '',
    quantity: holding?.quantity != null ? quantityToInput(holding.quantity, locale) : '',
    costPerUnit:
      holding?.costPerUnitMinor != null
        ? minorToInputText(holding.costPerUnitMinor, currency, locale)
        : '',
    currentPrice:
      holding?.currentPriceMinor != null
        ? minorToInputText(holding.currentPriceMinor, currency, locale)
        : '',
    currentPriceAsOf: holding?.currentPriceAsOf ?? todayCivil(),
  };
}

export function HoldingEditorDialog({
  type,
  holding,
  accounts,
  operatingCurrency,
  onClose,
  onSaved,
}: HoldingEditorDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const editing = holding !== undefined;
  const [form, setForm] = useState<FormState>(() =>
    initialState(type, holding, operatingCurrency, locale),
  );
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [subscribeRequired, setSubscribeRequired] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const loans = accounts.filter((a) => a.type === 'LOAN');
  const { dirty } = useDirty(form);
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  function collectCreate(): HoldingCreate | null {
    const errors: Partial<Record<FieldKey, string>> = {};
    const name = form.name.trim();
    if (name.length === 0) errors.name = t('holdings.errorValidation');
    const body: HoldingCreate = { name, type, subtype: form.subtype };
    if (form.description.trim().length > 0) body.description = form.description.trim();
    if (form.acquiredOn.length > 0) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(form.acquiredOn))
        errors.acquiredOn = t('holdings.errorValidation');
      else body.acquiredOn = form.acquiredOn;
    }
    if (type === 'FIXED_ASSET') {
      if (form.acquisitionCost.trim().length > 0) {
        const cost = parseMoneyToMinor(form.acquisitionCost, operatingCurrency, locale);
        if (cost === null) errors.acquisitionCostMinor = t('holdings.errorValidation');
        else body.acquisitionCostMinor = cost;
      }
      if (form.valuation.trim().length > 0) {
        const val = parseMoneyToMinor(form.valuation, operatingCurrency, locale);
        if (val === null) errors.valuationMinor = t('holdings.errorValidation');
        else body.valuationMinor = val;
      }
      if (form.valuationOn.length > 0) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(form.valuationOn))
          errors.valuationOn = t('holdings.errorValidation');
        else body.valuationOn = form.valuationOn;
      }
      if (form.rate.trim().length > 0) {
        const bp = percentToBp(form.rate, locale);
        if (bp === null) errors.annualRateBp = t('holdings.errorValidation');
        else body.annualRateBp = bp;
      }
      if (form.financedByAccountId.length > 0) {
        body.financedByAccountId = Number(form.financedByAccountId);
      }
    } else {
      if (form.symbol.trim().length > 0) body.symbol = form.symbol.trim().toUpperCase();
      const qty = parseQuantity(form.quantity, locale);
      if (qty === null) errors.quantity = t('holdings.errorValidation');
      else body.quantity = qty;
      if (form.costPerUnit.trim().length > 0) {
        const cpu = parseMoneyToMinor(form.costPerUnit, operatingCurrency, locale);
        if (cpu === null) errors.costPerUnitMinor = t('holdings.errorValidation');
        else body.costPerUnitMinor = cpu;
      }
      if (form.currentPrice.trim().length > 0) {
        const price = parseMoneyToMinor(form.currentPrice, operatingCurrency, locale);
        if (price === null) errors.currentPriceMinor = t('holdings.errorValidation');
        else body.currentPriceMinor = price;
      }
      if (form.currentPriceAsOf.length > 0) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(form.currentPriceAsOf)) {
          errors.currentPriceAsOf = t('holdings.errorValidation');
        } else body.currentPriceAsOf = form.currentPriceAsOf;
      }
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return null;
    return body;
  }

  function collectPatch(): HoldingPatch | null {
    if (holding === undefined) return null;
    const errors: Partial<Record<FieldKey, string>> = {};
    const body: HoldingPatch = {};
    const name = form.name.trim();
    if (name.length === 0) errors.name = t('holdings.errorValidation');
    else if (name !== holding.name) body.name = name;
    const description = form.description.trim();
    if (description !== (holding.description ?? '')) {
      body.description = description.length > 0 ? description : '';
    }
    if (form.acquiredOn.length > 0 && !/^\d{4}-\d{2}-\d{2}$/.test(form.acquiredOn)) {
      errors.acquiredOn = t('holdings.errorValidation');
    } else if (form.acquiredOn.length > 0 && form.acquiredOn !== (holding.acquiredOn ?? '')) {
      body.acquiredOn = form.acquiredOn;
    }
    if (type === 'FIXED_ASSET') {
      if (form.acquisitionCost.trim().length > 0) {
        const cost = parseMoneyToMinor(form.acquisitionCost, operatingCurrency, locale);
        if (cost === null) errors.acquisitionCostMinor = t('holdings.errorValidation');
        else if (cost !== holding.acquisitionCostMinor) body.acquisitionCostMinor = cost;
      }
      if (form.rate.trim().length > 0) {
        const bp = percentToBp(form.rate, locale);
        if (bp === null) errors.annualRateBp = t('holdings.errorValidation');
        else if (bp !== holding.annualRateBp) body.annualRateBp = bp;
      }
      const loanId =
        form.financedByAccountId.length > 0 ? Number(form.financedByAccountId) : undefined;
      if (loanId !== (holding.financedByAccountId ?? undefined)) {
        body.financedByAccountId = loanId ?? null;
      }
    } else if (form.symbol.trim().toUpperCase() !== (holding.symbol ?? '')) {
      body.symbol = form.symbol.trim().toUpperCase();
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return null;
    return body;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setSubmitMessage(null);
    try {
      if (editing && holding !== undefined) {
        const body = collectPatch();
        if (body === null) {
          guard.focusErrors();
          return;
        }
        setSubmitting(true);
        await holdingsApi.patch(holding.id, body);
      } else {
        const body = collectCreate();
        if (body === null) {
          guard.focusErrors();
          return;
        }
        setSubmitting(true);
        await holdingsApi.create(body);
      }
      await onSaved();
      onClose();
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else if (isAccountNotFound(error)) {
        setFieldErrors((prev) => ({ ...prev, financedByAccountId: t('holdings.loanInvalid') }));
        setSubmitMessage(t('holdings.loanInvalid'));
        guard.focusErrors();
      } else if (isApiError(error) && error.code === ErrorCodes.VALIDATION_FAILED) {
        const next: Partial<Record<FieldKey, string>> = {};
        const result = applyBackendFields(
          error,
          HOLDING_FIELDS,
          (field, message) => {
            next[field] = message;
          },
          t,
          guard.focusErrors,
        );
        setFieldErrors((prev) => ({ ...prev, ...next }));
        setSubmitMessage(validationAlert(result, error, t, t('holdings.errorValidation')));
      } else {
        setSubmitMessage(t('holdings.errorGeneric'));
        guard.focusErrors();
      }
      setSubmitting(false);
    }
  }

  const title = editing
    ? t('holdings.editTitle', { name: holding.name })
    : type === 'FIXED_ASSET'
      ? t('holdings.addAsset')
      : t('holdings.addTradable');

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      fullWidth
      maxWidth="sm"
      slotProps={{ transition: { onEntered: () => nameRef.current?.focus() } }}
    >
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <DialogTitle id={TITLE_ID}>{title}</DialogTitle>
        <DialogContent>
          {subscribeRequired ? (
            <SubscribeInPlace onClose={onClose} />
          ) : (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {submitMessage !== null && <Alert severity="error">{submitMessage}</Alert>}
              {editing ? (
                <Stack direction="row" sx={{ gap: 0.75 }}>
                  <Chip size="small" label={t(`holdings.type.${type}`)} />
                  <KindChip neutral label={t(`holdings.subtype.${form.subtype}`)} />
                </Stack>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="holding-subtype">{t('holdings.subtypeLabel')}</InputLabel>
                  <Select
                    labelId="holding-subtype"
                    label={t('holdings.subtypeLabel')}
                    value={form.subtype}
                    onChange={(event) => set('subtype', event.target.value as HoldingSubtype)}
                  >
                    {SUBTYPES_BY_HOLDING_TYPE[type].map((subtype) => (
                      <MenuItem key={subtype} value={subtype}>
                        {t(`holdings.subtype.${subtype}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                label={t('holdings.nameLabel')}
                value={form.name}
                onChange={(event) => set('name', event.target.value)}
                inputRef={nameRef}
                required
                fullWidth
                error={fieldErrors.name !== undefined}
                helperText={fieldErrors.name}
                slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
              />
              {type === 'FIXED_ASSET' ? (
                <>
                  <TextField
                    label={t('holdings.descriptionFixed')}
                    value={form.description}
                    onChange={(event) => set('description', event.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    error={fieldErrors.description !== undefined}
                    helperText={fieldErrors.description}
                    slotProps={{ htmlInput: { maxLength: DESC_MAX } }}
                  />
                  <TextField
                    type="date"
                    label={t('holdings.acquiredOnLabel')}
                    value={form.acquiredOn}
                    onChange={(event) => set('acquiredOn', event.target.value)}
                    fullWidth
                    error={fieldErrors.acquiredOn !== undefined}
                    helperText={fieldErrors.acquiredOn}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    label={t('holdings.acquisitionCostLabel')}
                    value={form.acquisitionCost}
                    onChange={(event) => set('acquisitionCost', event.target.value)}
                    fullWidth
                    error={fieldErrors.acquisitionCostMinor !== undefined}
                    helperText={fieldErrors.acquisitionCostMinor}
                  />
                  <TextField
                    label={t('holdings.valuationLabel')}
                    value={form.valuation}
                    onChange={(event) => set('valuation', event.target.value)}
                    fullWidth
                    disabled={editing}
                    error={fieldErrors.valuationMinor !== undefined}
                    helperText={
                      fieldErrors.valuationMinor ?? (editing ? t('holdings.valueHint') : undefined)
                    }
                  />
                  <TextField
                    type="date"
                    label={t('holdings.valuationOnLabel')}
                    value={form.valuationOn}
                    onChange={(event) => set('valuationOn', event.target.value)}
                    fullWidth
                    disabled={editing}
                    error={fieldErrors.valuationOn !== undefined}
                    helperText={fieldErrors.valuationOn}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    label={t('holdings.rateLabel')}
                    value={form.rate}
                    onChange={(event) => set('rate', event.target.value)}
                    fullWidth
                    error={fieldErrors.annualRateBp !== undefined}
                    helperText={fieldErrors.annualRateBp ?? t('holdings.rateHelp')}
                  />
                  <FormControl fullWidth error={fieldErrors.financedByAccountId !== undefined}>
                    <InputLabel id="holding-loan">{t('holdings.financedByLabel')}</InputLabel>
                    <Select
                      labelId="holding-loan"
                      label={t('holdings.financedByLabel')}
                      value={form.financedByAccountId}
                      onChange={(event) => set('financedByAccountId', event.target.value)}
                    >
                      <MenuItem value="">{t('holdings.financedByNone')}</MenuItem>
                      {loans.map((loan) => (
                        <MenuItem key={loan.id} value={String(loan.id)}>
                          {loan.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.financedByAccountId !== undefined && (
                      <FormHelperText>{fieldErrors.financedByAccountId}</FormHelperText>
                    )}
                  </FormControl>
                </>
              ) : (
                <>
                  <TextField
                    label={t('holdings.symbolLabel')}
                    value={form.symbol}
                    onChange={(event) => set('symbol', event.target.value)}
                    onBlur={() => set('symbol', form.symbol.toUpperCase())}
                    fullWidth
                    error={fieldErrors.symbol !== undefined}
                    helperText={fieldErrors.symbol}
                    slotProps={{ htmlInput: { maxLength: SYMBOL_MAX } }}
                  />
                  <TextField
                    label={t('holdings.quantityLabel')}
                    value={form.quantity}
                    onChange={(event) => set('quantity', event.target.value)}
                    fullWidth
                    disabled={editing}
                    required={!editing}
                    error={fieldErrors.quantity !== undefined}
                    helperText={
                      fieldErrors.quantity ?? (editing ? t('holdings.markHint') : undefined)
                    }
                  />
                  <TextField
                    type="date"
                    label={t('holdings.acquiredOnLabel')}
                    value={form.acquiredOn}
                    onChange={(event) => set('acquiredOn', event.target.value)}
                    fullWidth
                    error={fieldErrors.acquiredOn !== undefined}
                    helperText={fieldErrors.acquiredOn}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    label={t('holdings.costPerUnitLabel')}
                    value={form.costPerUnit}
                    onChange={(event) => set('costPerUnit', event.target.value)}
                    fullWidth
                    disabled={editing}
                    error={fieldErrors.costPerUnitMinor !== undefined}
                    helperText={fieldErrors.costPerUnitMinor}
                  />
                  <TextField
                    label={t('holdings.currentPriceLabel')}
                    value={form.currentPrice}
                    onChange={(event) => set('currentPrice', event.target.value)}
                    fullWidth
                    disabled={editing}
                    error={fieldErrors.currentPriceMinor !== undefined}
                    helperText={
                      fieldErrors.currentPriceMinor ??
                      (editing ? t('holdings.markHint') : undefined)
                    }
                  />
                  <TextField
                    type="date"
                    label={t('holdings.currentPriceAsOfLabel')}
                    value={form.currentPriceAsOf}
                    onChange={(event) => set('currentPriceAsOf', event.target.value)}
                    fullWidth
                    disabled={editing}
                    error={fieldErrors.currentPriceAsOf !== undefined}
                    helperText={fieldErrors.currentPriceAsOf}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        {!subscribeRequired && (
          <DialogActions>
            <Button onClick={guard.requestClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <SubmitButton type="submit" loading={submitting}>
              {t('common.save')}
            </SubmitButton>
          </DialogActions>
        )}
      </form>
    </GuardedDialog>
  );
}
